import { withFormik } from 'formik'
import * as Yup from 'yup'

import { getCookie } from 'core/infra/cookie'

import Intl from 'legacy/components/I18n/i18n'
import { createAccountCarrier, createAccountCarrierIntermodal } from 'legacy/services/carriers'
import { submitHubspotForm } from 'legacy/services/hubspot'
import analytics from 'legacy/utils/analytics'
import isE2EAccount from 'legacy/utils/e2eAccountValidation'
import sentry from 'legacy/utils/sentry'
import storage from 'legacy/utils/storage'

import CarrierInnerForm from './CarrierInnerForm'

export function createHubspotFormFields(data) {
  const { firstName, lastName, email, dot_number, mc_number } = data
  return {
    fields: [
      { name: 'firstname', value: firstName },
      { name: 'lastname', value: lastName },
      { name: 'email', value: email },
      { name: 'dot_number', value: dot_number },
      { name: 'mc_number', value: mc_number },
    ],
    context: {
      hutk: getCookie('hubspotutk'),
      pageName: document?.location.pathname,
      pageUri: document?.location.toString(),
    },
  }
}

function equalTo(ref, msg) {
  return Yup.mixed().test({
    name: 'equalTo',
    exclusive: false,
    // eslint-disable-next-line no-template-curly-in-string
    message: msg || '${path} must be the same as ${reference}',
    params: {
      reference: ref.path,
    },
    test: function (value) {
      return value === this.resolve(ref)
    },
  })
}

Yup.addMethod(Yup.string, 'equalTo', equalTo)

const VALIDATION_SCHEMA = {
  firstName: Yup.string().required(Intl.get('ls.components.Validation.labels.required')),
  lastName: Yup.string().required(Intl.get('ls.components.Validation.labels.required')),
  password: Yup.string().min(8).required(Intl.get('ls.components.Validation.labels.required')),
  repeatPassword: Yup.string()
    .min(8)
    .equalTo(Yup.ref('password'), Intl.get('ls.components.Validation.labels.passwordMismatch'))
    .required(Intl.get('ls.components.Validation.labels.required')),
  email: Yup.string()
    .email(Intl.get('ls.components.Validation.labels.emailInvalid'))
    .required(Intl.get('ls.components.Validation.labels.required')),
  dot_number: Yup.string().required(Intl.get('ls.components.Validation.labels.required')),
  mc_number: Yup.string().required(Intl.get('ls.components.Validation.labels.required')),
  equipmentType: Yup.string().required(Intl.get('ls.components.Validation.labels.equipment')),
  scac: Yup.string().when('equipmentType', {
    is: 'drayage',
    then: () =>
      Yup.string()
        .required(Intl.get('ls.components.Validation.labels.required'))
        .min(2)
        .max(4)
        .matches(/^[a-zA-Z]+$/, Intl.get('ls.components.Validation.labels.scac')),
  }),
  uiia: Yup.bool().when('equipmentType', {
    is: 'drayage',
    then: () =>
      Yup.boolean()
        .required(Intl.get('ls.components.Validation.labels.uiia'))
        .oneOf([true], Intl.get('ls.components.Validation.labels.uiia')),
  }),
  emodal: Yup.bool().when('equipmentType', {
    is: 'drayage',
    then: () =>
      Yup.boolean()
        .required(Intl.get('ls.components.Validation.labels.portAccess'))
        .oneOf([true], Intl.get('ls.components.Validation.labels.portAccess')),
  }),
}

function buildPostPayload(values) {
  const isIntermodal = values.equipmentType === 'drayage'

  const payload = {
    account: {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email.toLowerCase(),
      password: values.password,
    },
  }

  payload.dot_number = values.dot_number

  if (values.mc_number) {
    payload.mc_number = values.mc_number
  }

  if (values.lane) {
    payload.extra_actions = [
      {
        type: 'preferred-lane',
        action: 'add',
        data: values.lane,
      },
    ]
  }

  if (isIntermodal) {
    payload.drayage = {
      scac_code: values.scac.toUpperCase(),
      has_power_refeer: false,
      has_valid_emodal: values.emodal,
      has_valid_uiia: values.uiia,
    }
  }

  return payload
}

function getErrorMessage(error) {
  const { response } = error

  let errorMessage = 'Signup could not be completed, please contact us.'

  if (response.hasOwnProperty('data')) {
    const { data = '' } = response

    if (data.hasOwnProperty('errors')) {
      const { errors } = data
      const accountError = errors[Object.keys(errors)[0]]
      const key = Object.keys(accountError)[0]

      errorMessage = accountError[key]
    } else if (data.hasOwnProperty('error_description')) {
      errorMessage = data.error_description
    }
  }

  return errorMessage
}

async function createAccount(values, { setSubmitting, setErrors, setStatus }) {
  const isIntermodal = values.equipmentType === 'drayage'
  const type = isIntermodal ? 'intermodal' : 'ftl'

  try {
    const payload = buildPostPayload(values)

    const createMethod = isIntermodal ? createAccountCarrierIntermodal : createAccountCarrier
    await createMethod(payload)

    setStatus({ submitted: true })
    const datetime = new Date()
    storage.set('ls-signup-time', datetime)

    setSubmitting(false)

    if (!isE2EAccount(values.email)) {
      analytics.track('Signup / Carrier / Submitted', { type })
    }
  } catch (error) {
    const { response } = error

    setErrors({ server: getErrorMessage(error) })
    setSubmitting(false)

    analytics.track('Signup / Carrier / Submit / Error', {
      type,
      status: response?.status,
      description: response?.data?.error_description,
    })
  }
}

function submitHubspot(values) {
  const hubspotData = createHubspotFormFields(values)

  submitHubspotForm(hubspotData, 'carrier').catch(error => {
    if (error.response.data) {
      sentry.log(new Error('Failed submit hubspot data - response', { cause: error.response.data }))
    } else {
      sentry.log(new Error('Failed submit hubspot data', { cause: error.message }))
    }
  })
}

export const CarrierForm = withFormik({
  initialValues: { equipment: 'ftl' },
  mapPropsToValues: ({ mc = '', dot = '', email = '', lane }) => ({
    firstName: '',
    lastName: '',
    dot_number: dot,
    mc_number: mc,
    email: email,
    password: '',
    repeatPassword: '',
    equipmentType: 'ftl',
    scac: '',
    uiia: '',
    emodal: '',
    lane,
  }),
  validationSchema: Yup.object().shape(VALIDATION_SCHEMA),
  handleSubmit: async (values, props) => {
    await createAccount(values, props)
    submitHubspot(values)
  },
})

export default CarrierForm(CarrierInnerForm)
